import jsPDF from 'jspdf';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const loadLogo = async (importadoraId) => {
  try {
    const res = await axios.get(`${API}/importadoras/${importadoraId}/logo`);
    return res.data.base64;
  } catch { return null; }
};

const drawSection = (doc, title, y, margin, contentWidth) => {
  doc.setFillColor(0, 51, 102);
  doc.rect(margin, y, contentWidth, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text(title, margin + 3, y + 5);
  doc.setTextColor(0, 0, 0);
  return y + 12;
};

const drawRow = (doc, label, value, y, margin) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text(label, margin + 2, y);
  doc.setFont('helvetica', 'normal');
  const maxW = 120;
  const lines = doc.splitTextToSize(value || 'N/A', maxW);
  doc.text(lines, margin + 42, y);
  return y + 5 * lines.length;
};

export const generateSalesOrderPDF = async (business, client, importadora) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // ========== CARGAR LOGO ==========
  let logo = null;
  if (importadora?.id) {
    logo = await loadLogo(importadora.id);
  }

  // ========== HEADER: Logo izquierda + N° Cierre / Fecha derecha ==========
  doc.setFillColor(0, 51, 102);
  doc.rect(0, 0, pageWidth, 3, 'F');

  if (logo) {
    try {
      doc.addImage(logo, 'AUTO', margin, y, 50, 20);
    } catch (e) { console.warn('Error logo:', e); }
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0, 51, 102);
    doc.text(importadora?.alias || importadora?.razon_social || '', margin, y + 12);
  }

  const rightX = pageWidth - margin;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(0, 51, 102);
  doc.text('ORDEN DE VENTA', rightX, y + 5, { align: 'right' });
  doc.setFontSize(12);
  doc.text(`N° ${business.business_number}`, rightX, y + 12, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text(`Cierre: ${business.cierre || 'N/A'}`, rightX, y + 18, { align: 'right' });
  doc.text(`Fecha: ${formatDate(business.fecha)}`, rightX, y + 23, { align: 'right' });

  doc.setTextColor(0, 0, 0);
  y += 28;

  doc.setDrawColor(0, 51, 102);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 6;

  // ========== VENDEDOR ==========
  y = drawSection(doc, 'VENDEDOR', y, margin, contentWidth);
  y = drawRow(doc, 'Razón Social:', importadora?.razon_social || 'N/A', y, margin);
  y = drawRow(doc, 'RUT:', importadora?.rut || 'N/A', y, margin);
  y = drawRow(doc, 'Giro:', importadora?.giro || 'N/A', y, margin);
  y = drawRow(doc, 'Dirección:', importadora?.direccion || 'N/A', y, margin);
  y = drawRow(doc, 'Comuna:', importadora?.comuna || 'N/A', y, margin);

  // Contactos fijos del vendedor (lado a lado)
  y += 2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text('Contacto:', margin + 2, y);
  doc.setFont('helvetica', 'normal');

  const col1X = margin + 42;
  const col2X = margin + 105;

  doc.setFont('helvetica', 'bold');
  doc.text('JULIAN QUINTAS', col1X, y);
  doc.text('MARCO ANTONIO QUINTAS', col2X, y);
  y += 4.5;
  doc.setFont('helvetica', 'normal');
  doc.text('+56 9 8904 0278', col1X, y);
  doc.text('+56 9 8595 4000', col2X, y);
  y += 8;

  // ========== COMPRADOR ==========
  y = drawSection(doc, 'COMPRADOR', y, margin, contentWidth);
  y = drawRow(doc, 'Razón Social:', client?.razon_social || 'N/A', y, margin);
  y = drawRow(doc, 'RUT:', client ? `${client.rut}-${client.rut_dv}` : 'N/A', y, margin);
  y = drawRow(doc, 'Giro:', client?.giro || 'N/A', y, margin);
  y = drawRow(doc, 'Dirección:', client?.direccion || 'N/A', y, margin);
  y = drawRow(doc, 'Comuna:', client?.comuna || 'N/A', y, margin);
  y = drawRow(doc, 'Ciudad:', client?.ciudad || 'N/A', y, margin);
  y = drawRow(doc, 'Contacto:', `${business.contacto || 'N/A'}`, y, margin);
  y += 4;

  // ========== DATOS DEL NEGOCIO ==========
  y = drawSection(doc, 'DATOS DEL NEGOCIO', y, margin, contentWidth);
  y = drawRow(doc, 'Producto:', `${business.producto || ''} - ${business.calidad || ''}`, y, margin);
  y = drawRow(doc, 'Cantidad:', `${business.cantidad_toneladas || 0} Toneladas`, y, margin);
  y = drawRow(doc, 'Precio:', `USD $${(business.precio_unitario || 0).toLocaleString('es-CL', { minimumFractionDigits: 2 })} / Tonelada`, y, margin);
  y = drawRow(doc, 'Camiones:', `${business.cantidad_camiones || 0}`, y, margin);
  y = drawRow(doc, 'Cronograma:', business.cronograma || 'N/A', y, margin);
  y = drawRow(doc, 'Dir. Entrega:', business.direccion_entrega || 'N/A', y, margin);
  y = drawRow(doc, 'Cond. de Pago:', business.condicion_pago || 'N/A', y, margin);
  y = drawRow(doc, 'Banco:', business.banco || 'N/A', y, margin);
  y = drawRow(doc, 'Cuenta Cte:', business.cuenta_corriente || 'N/A', y, margin);
  y = drawRow(doc, 'Emails:', business.emails_comprobantes || 'N/A', y, margin);

  // ========== CARGAR FIRMA VENDEDOR ==========
  let firmaBase64 = null;
  try {
    const firmaRes = await axios.get(`${API}/config/firma_vendedor`);
    firmaBase64 = firmaRes.data.base64;
  } catch { /* sin firma */ }

  // ========== FIRMAS ==========
  const signY = Math.max(y + 20, pageHeight - 50);
  if (signY < pageHeight - 20) {
    // Firma vendedor con imagen
    if (firmaBase64) {
      try {
        doc.addImage(firmaBase64, 'JPEG', margin + 10, signY - 18, 40, 25);
      } catch (e) { console.warn('Error firma:', e); }
    }
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.line(margin, signY, margin + 65, signY);
    doc.line(pageWidth - margin - 65, signY, pageWidth - margin, signY);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Firma Vendedor', margin + 18, signY + 5);
    doc.text('Firma Comprador', pageWidth - margin - 48, signY + 5);
  }

  // ========== PIE ==========
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text(`Documento generado el ${new Date().toLocaleString('es-CL')} - CIG`, pageWidth / 2, pageHeight - 8, { align: 'center' });

  // Generar nombre de archivo usando alias si existe
  const clientName = client?.alias || client?.razon_social || 'Cliente';
  const sanitizedName = clientName.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_');
  doc.save(`OV_${business.business_number}_${sanitizedName}.pdf`);
};

export default { generateSalesOrderPDF };
